import {createSlice} from "@reduxjs/toolkit";
import {CONTENT_TYPES} from "./content-type-registry";

const EMPTY_ENTRY = Object.freeze({
    items: [],
    total: 0,
    status: "idle",
    error: null,
    loadedAt: null,
    queryKey: null,
});

const initialState = {
    byUid: Object.fromEntries(
        CONTENT_TYPES.map(({uid}) => [uid, {...EMPTY_ENTRY}])
    ),
};

function ensureEntry(state, uid) {
    if (!state.byUid[uid]) state.byUid[uid] = {...EMPTY_ENTRY};
    return state.byUid[uid];
}

function entityKey(entity) {
    return entity?.documentId ?? entity?.id;
}

const contentTypeDataSlice = createSlice({
    name: "contentTypeData",
    initialState,
    reducers: {
        contentTypeRequestStarted(state, action) {
            const entry = ensureEntry(state, action.payload.uid);
            entry.status = "loading";
            entry.error = null;
            entry.queryKey = action.payload.queryKey;
        },
        contentTypeRequestSucceeded(state, action) {
            const entry = ensureEntry(state, action.payload.uid);
            entry.items = action.payload.items || [];
            entry.total = action.payload.total ?? entry.items.length;
            entry.status = "succeeded";
            entry.error = null;
            entry.loadedAt = Date.now();
            entry.queryKey = action.payload.queryKey;
        },
        contentTypeRequestFailed(state, action) {
            const entry = ensureEntry(state, action.payload.uid);
            entry.status = "failed";
            entry.error = action.payload.error || "content-type-load-error";
        },
        upsertContentTypeItem(state, action) {
            const entry = ensureEntry(state, action.payload.uid);
            const item = action.payload.item;
            const key = entityKey(item);
            if (key == null) return;

            const index = entry.items.findIndex((current) => entityKey(current) === key);
            if (index >= 0) entry.items[index] = {...entry.items[index], ...item};
            else {
                entry.items.unshift(item);
                entry.total += 1;
            }
            entry.loadedAt = Date.now();
        },
        removeContentTypeItem(state, action) {
            const entry = ensureEntry(state, action.payload.uid);
            const before = entry.items.length;
            entry.items = entry.items.filter((item) => entityKey(item) !== action.payload.documentId);
            if (entry.items.length !== before) entry.total = Math.max(0, entry.total - 1);
            entry.loadedAt = Date.now();
        },
        invalidateContentType(state, action) {
            const entry = ensureEntry(state, action.payload);
            entry.status = "idle";
            entry.queryKey = null;
        },
        clearContentTypeData() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase("auth/login/fulfilled", () => initialState)
            .addCase("auth/logout/fulfilled", () => initialState);
    },
});

export const {
    contentTypeRequestStarted,
    contentTypeRequestSucceeded,
    contentTypeRequestFailed,
    upsertContentTypeItem,
    removeContentTypeItem,
    invalidateContentType,
    clearContentTypeData,
} = contentTypeDataSlice.actions;

export const selectContentTypeEntry = (state, uid) => state.contentTypeData?.byUid?.[uid] || EMPTY_ENTRY;
export const selectContentTypeItems = (state, uid) => selectContentTypeEntry(state, uid).items;

export default contentTypeDataSlice.reducer;
